import { usePasswordResetMutation } from '@codezo/graphql';
import { useThemeColors } from '@codezo/theme';
import { Alert, Box, Button, Paper, PasswordInput, Stack, Text, Title } from '@mantine/core';
import { IconAlertCircle, IconLock } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Logo } from '../components/common/Logo';

export function ResetPasswordNewPage() {
	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [error, setError] = useState<string | null>(null);
	const navigate = useNavigate();
	const location = useLocation();

	const colors = useThemeColors();

	// Get token and validation status from navigation state
	const token = location.state?.token as string | undefined;
	const isValidated = location.state?.validated as boolean | undefined;

	useEffect(() => {
		if (!token || !isValidated) {
			navigate('/auth/forgot-password');
		}
	}, [token, isValidated, navigate]);

	const passwordsMatch = password === confirmPassword && password.length > 0;

	const [resetPassword, { loading }] = usePasswordResetMutation({
		onCompleted: (data) => {
			if (data.passwordReset.success) {
				// Navigate directly to login page
				navigate('/auth/login');
			}
		},
		onError: (err) => {
			setError(err.message || 'Failed to reset password. Please try again.');
		},
	});

	const handleReset = async () => {
		if (!token || !passwordsMatch) return;

		setError(null);

		try {
			await resetPassword({
				variables: {
					input: {
						token,
						password,
					},
				},
			});
		} catch {
			// Error is already handled by onError callback
			// Error handled by mutation onError
		}
	};

	return (
		<Box style={{ position: 'relative' }}>
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5 }}
			>
				<Paper
					p={{ base: 'xs', sm: 'xl' }}
					radius="md"
					miw={{ base: 'calc(100vw - 20px)', xs: 480 }}
				>
					<Stack>
						<Stack gap="sm" ta="center">
							<Logo height={40} />
							<div>
								<Title mb={4}>Create New Password</Title>
								<Text size="sm" c={colors.textMuted}>
									Choose a strong password for your account
								</Text>
							</div>
						</Stack>

						{error && (
							<Alert
								icon={<IconAlertCircle size={20} color={colors.error} />}
								color="error"
							>
								{error}
							</Alert>
						)}

						<Stack>
							<PasswordInput
								label="New Password"
								placeholder="Enter new password"
								leftSection={<IconLock size={18} />}
								value={password}
								onChange={(e) => setPassword(e.currentTarget.value)}
								disabled={loading}
								required
							/>

							<PasswordInput
								label="Confirm Password"
								placeholder="Confirm new password"
								leftSection={<IconLock size={18} />}
								value={confirmPassword}
								onChange={(e) => setConfirmPassword(e.currentTarget.value)}
								disabled={loading}
								error={
									confirmPassword && !passwordsMatch
										? "Passwords don't match"
										: undefined
								}
								onKeyDown={(e) => {
									if (e.key === 'Enter' && passwordsMatch) {
										handleReset();
									}
								}}
								required
							/>
						</Stack>

						<Button
							fullWidth
							size="sm"
							onClick={handleReset}
							loading={loading}
							disabled={!passwordsMatch || password.length === 0}
						>
							Reset Password
						</Button>
					</Stack>
				</Paper>
			</motion.div>
		</Box>
	);
}
